import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { handleFormSubmit } from "@/utils/handleFormSubmit";
import { isValidEmail } from "@/utils/validation";
import { getWhatsAppLink } from "@/utils/whatsapp";
import { CheckCircle, ArrowRight, RotateCcw } from "lucide-react";

interface PrintQuizProps {
  embedded?: boolean;
  onComplete?: () => void;
}

const QUESTIONS = [
  {
    id: "industry",
    question: "What industry are you in?",
    options: ["School/Education", "Corporate/Business", "Event/Religious", "Other"],
  },
  {
    id: "goal",
    question: "What is your primary goal?",
    options: [
      "Look more professional",
      "Increase enrollment/awareness",
      "Launch event",
      "Reorder",
      "Complete rebrand",
    ],
  },
  {
    id: "budget",
    question: "What is your approximate budget?",
    options: ["Under ₦100k", "₦100k-₦500k", "₦500k-₦2M", "₦2M+"],
  },
  {
    id: "timeline",
    question: "When do you need it?",
    options: ["Under 2 weeks", "1 month", "2-3 months", "Just budgeting"],
  },
];

function getRecommendation(answers: Record<string, string>) {
  const { industry, goal, budget, timeline } = answers;

  if (goal === "Complete rebrand" || budget === "₦2M+") {
    return {
      package: "Full Rebrand Package",
      price: "₦1.5M - ₦5M",
      items: ["Brand guide", "Uniforms", "Signage", "Stationery", "Digital assets"],
    };
  }
  if (industry === "School/Education" && (goal === "Look more professional" || goal === "Increase enrollment/awareness")) {
    return {
      package: "School Branding Essentials",
      price: "₦300k - ₦1.2M",
      items: ["Uniforms", "ID Cards", "Signage", "Certificates", "Banners"],
    };
  }
  if (industry === "Corporate/Business" && goal === "Look more professional") {
    return {
      package: "Corporate Starter Kit",
      price: "₦200k - ₦800k",
      items: ["Business cards", "Letterheads", "Branded mugs", "Lanyards", "Notebooks"],
    };
  }
  if (goal === "Launch event") {
    return {
      package: "Event Branding Bundle",
      price: "₦150k - ₦600k",
      items: ["Banners", "Programs", "Souvenirs", "Signage", "Photobooth backdrop"],
    };
  }
  if (budget === "Under ₦100k") {
    return {
      package: "Quick Print Bundle",
      price: "₦50k - ₦100k",
      items: ["Business cards", "Flyers", "Stickers"],
    };
  }
  return {
    package: "Custom Branding Package",
    price: "₦200k - ₦1M",
    items: ["Consultation", "Custom items based on needs"],
  };
}

export default function PrintQuiz({ embedded, onComplete }: PrintQuizProps) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResult, setShowResult] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const handleAnswer = (answer: string) => {
    const q = QUESTIONS[step];
    const newAnswers = { ...answers, [q.id]: answer };
    setAnswers(newAnswers);
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      setShowResult(true);
      onComplete?.();
    }
  };

  const handleRestart = () => {
    setStep(0);
    setAnswers({});
    setShowResult(false);
    setEmail("");
    setEmailError("");
    setEmailSent(false);
  };

  const handleEmailSubmit = async () => {
    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email");
      return;
    }
    setEmailError("");
    await handleFormSubmit({ email, answers, recommendation: getRecommendation(answers) }, "quiz_result");
    setEmailSent(true);
  };

  if (showResult) {
    const rec = getRecommendation(answers);
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-border text-center">
        <CheckCircle className="w-12 h-12 text-[#e85a1f] mx-auto mb-4" />
        <h3 className="text-2xl font-bold mb-2">Recommended: {rec.package}</h3>
        <p className="text-lg text-foreground/70 mb-4">Estimated: {rec.price}</p>
        <ul className="text-sm text-foreground/60 mb-6 space-y-1">
          {rec.items.map((item) => (
            <li key={item}>• {item}</li>
          ))}
        </ul>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
          <Button asChild className="bg-[#25D366] hover:bg-[#128C7E] text-white">
            <a href={getWhatsAppLink(`Hi Oyinbatfo! I took your quiz and got recommended the ${rec.package}. Can I get a detailed quote?`)} target="_blank" rel="noopener noreferrer">
              Get Quote on WhatsApp
            </a>
          </Button>
          <Button asChild variant="outline" className="text-[#e85a1f] border-[#e85a1f]">
            <a href={import.meta.env.VITE_CALENDLY_URL || "#"} target="_blank" rel="noopener noreferrer">
              Book Free 15-Min Call
            </a>
          </Button>
        </div>
        <Link to="/portfolio" className="text-sm text-[#e85a1f] underline mb-6 inline-block">See Examples</Link>
        {!emailSent ? (
          <div className="border-t border-border pt-6 mt-6">
            <p className="text-sm text-foreground/70 mb-3">Want this sent to your email?</p>
            <div className="flex gap-2 max-w-sm mx-auto">
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button onClick={handleEmailSubmit} className="bg-[#e85a1f] hover:bg-[#d14d15] text-white">Send</Button>
            </div>
            {emailError && <p className="text-sm text-red-500 mt-2">{emailError}</p>}
          </div>
        ) : (
          <p className="text-green-600 text-sm mt-4">Sent to your email!</p>
        )}
        <button onClick={handleRestart} className="mt-6 text-sm text-foreground/50 flex items-center gap-1 mx-auto hover:text-foreground">
          <RotateCcw className="w-3 h-3" /> Retake Quiz
        </button>
      </div>
    );
  }

  const currentQ = QUESTIONS[step];

  return (
    <div className={`bg-white rounded-xl shadow-lg p-6 md:p-8 border border-border ${embedded ? "" : "max-w-2xl mx-auto"}`}>
      <div className="mb-6">
        <div className="flex justify-between text-xs text-foreground/50 mb-2">
          <span>Question {step + 1} of {QUESTIONS.length}</span>
          <span>{Math.round(((step + 1) / QUESTIONS.length) * 100)}%</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-[#e85a1f] transition-all duration-300"
            style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
          />
        </div>
      </div>
      <h3 className="text-xl font-bold mb-6">{currentQ.question}</h3>
      <div className="space-y-3">
        {currentQ.options.map((option) => (
          <button
            key={option}
            onClick={() => handleAnswer(option)}
            className="w-full text-left p-4 rounded-lg border border-border hover:border-[#e85a1f] hover:bg-[#e85a1f]/5 transition-colors flex items-center justify-between"
          >
            <span>{option}</span>
            <ArrowRight className="w-4 h-4 text-foreground/30" />
          </button>
        ))}
      </div>
    </div>
  );
}

import { Link } from "react-router-dom";
